/**
 * Unified Realtime WebSocket Service (Singleton)
 *
 * Single WebSocket connection per user session that multiplexes all message types:
 * notifications, tasks, tickets, broadcasts.
 *
 * Replaces: notificationService, taskService, ticketService, broadcastService.
 *
 * Architecture:
 * - WS messages trigger Redux state updates (for bell badges, etc.)
 * - WS messages ALSO trigger RTK Query cache invalidation (for list/detail views)
 * - When disconnected, RTK Query polling kicks in as fallback
 * - On reconnect, all caches are invalidated to catch up
 */

import { getToken } from '../utils/tokenStorage';

// Dynamic store import to avoid circular dependency
const getStore = () => require('../../store').store;
const getBureauApi = () => require('../../portals/bureau/api/bureauApiSlice').bureauApi;

const getWsBaseUrl = () => {
  if (process.env.REACT_APP_WS_BASE_URL) return process.env.REACT_APP_WS_BASE_URL;
  if (window.location.hostname === 'fleetcamagile.com') return 'wss://fleetcamagile.com';
  return 'ws://localhost:8000';
};

class RealtimeService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;
    this.heartbeatInterval = null;
    this.userId = null;
    this.portal = null;
    this.visibilityTimeout = null;
  }

  /**
   * Initialize the unified WebSocket connection.
   * Called once on login via websocketServices.js.
   */
  initialize(userId, portal) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[Realtime] Already connected');
      return;
    }

    if (!userId) {
      console.log('[Realtime] No userId, skipping');
      return;
    }

    this.userId = userId;
    this.portal = portal || 'bureau';
    this.connect();
    this.setupVisibilityHandler();
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    const token = getToken();
    const url = `${getWsBaseUrl()}/ws/realtime/?token=${token || ''}&portal=${this.portal}`;
    console.log('[Realtime] Connecting to:', url.replace(/token=[^&]+/, 'token=***'));

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('[Realtime] Connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();

        const store = getStore();
        if (store) {
          store.dispatch({ type: 'realtime/setConnected', payload: true });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (err) {
          console.error('[Realtime] Parse error:', err);
        }
      };

      this.ws.onerror = (err) => {
        console.error('[Realtime] Error:', err);
        this.isConnected = false;
      };

      this.ws.onclose = (event) => {
        console.log(`[Realtime] Closed (code=${event.code})`);
        this.isConnected = false;
        this.stopHeartbeat();

        const store = getStore();
        if (store) {
          store.dispatch({ type: 'realtime/setConnected', payload: false });
        }

        this.scheduleReconnect();
      };
    } catch (err) {
      console.error('[Realtime] Connection error:', err);
      this.isConnected = false;
    }
  }

  /**
   * Route incoming messages to Redux + RTK Query cache invalidation.
   */
  handleMessage(data) {
    const store = getStore();
    if (!store?.dispatch) return;

    switch (data.type) {
      // ── Initial combined data ──────────────────────────────
      case 'initial_data':
        console.log(
          `[Realtime] Initial data: ${data.notifications?.notifications?.length || 0} notifs, ` +
          `${data.tasks?.length || 0} tasks, ${data.tickets?.length || 0} tickets, ` +
          `${data.broadcasts?.length || 0} broadcasts`
        );
        // Populate Redux slices
        store.dispatch({ type: 'notifications/setNotifications', payload: data.notifications?.notifications || [] });
        store.dispatch({ type: 'notifications/setUnreadCount', payload: data.notifications?.unread_count || 0 });
        store.dispatch({ type: 'notifications/setConnected', payload: true });
        store.dispatch({ type: 'tasks/setTasks', payload: data.tasks || [] });
        store.dispatch({ type: 'tasks/setConnected', payload: true });
        store.dispatch({ type: 'tickets/setTickets', payload: data.tickets || [] });
        store.dispatch({ type: 'tickets/setConnected', payload: true });
        store.dispatch({ type: 'broadcasts/setBroadcasts', payload: data.broadcasts || [] });
        store.dispatch({ type: 'broadcasts/setConnected', payload: true });
        break;

      // ── Notifications ──────────────────────────────────────
      case 'notification_update':
        if (data.action === 'created') {
          console.log('[Realtime] New notification:', data.notification?.title);
          store.dispatch({ type: 'notifications/addNotification', payload: data.notification });
          this._showBrowserPush(data.notification);
        } else if (data.action === 'updated') {
          store.dispatch({ type: 'notifications/updateNotification', payload: data.notification });
        } else if (data.action === 'deleted') {
          store.dispatch({ type: 'notifications/removeNotification', payload: { id: data.notification?.id } });
        }
        this._invalidateTags(['Notification', 'NotificationCount']);
        break;

      case 'notification_response':
        console.log('[Realtime] Response received:', data.notification_title);
        store.dispatch({
          type: 'notifications/updateNotificationResponse',
          payload: {
            notificationId: data.notification_id,
            notificationTitle: data.notification_title,
            response: data.response,
          },
        });
        this._invalidateTags(['Notification']);
        break;

      // ── Tasks ──────────────────────────────────────────────
      case 'task_update':
        if (data.action === 'created') {
          store.dispatch({ type: 'tasks/addTask', payload: data.task });
        } else if (data.action === 'deleted') {
          store.dispatch({ type: 'tasks/removeTask', payload: { id: data.task?.id } });
        } else {
          store.dispatch({ type: 'tasks/updateTask', payload: data.task });
        }
        this._invalidateTags(['Task', 'MyTasks', 'CreatedTasks', 'TaskStats']);
        break;

      // ── Tickets ────────────────────────────────────────────
      case 'ticket_update':
        if (data.action === 'created') {
          store.dispatch({ type: 'tickets/addTicket', payload: data.ticket });
        } else {
          store.dispatch({ type: 'tickets/updateTicket', payload: data.ticket });
        }
        this._invalidateTags(['SupportTicket']);
        break;

      // ── Broadcasts ─────────────────────────────────────────
      case 'broadcast_update':
        if (data.action === 'created') {
          console.log('[Realtime] New broadcast:', data.broadcast?.title);
          store.dispatch({ type: 'broadcasts/addBroadcast', payload: data.broadcast });
        } else {
          store.dispatch({ type: 'broadcasts/updateBroadcast', payload: data.broadcast });
        }
        this._invalidateTags(['Broadcast']);
        break;

      case 'heartbeat':
      case 'pong':
        break;

      default:
        console.log('[Realtime] Unknown message type:', data.type);
    }
  }

  /**
   * Invalidate RTK Query cache tags — triggers automatic refetch for subscribed components.
   */
  _invalidateTags(tags) {
    try {
      const store = getStore();
      const bureauApi = getBureauApi();
      if (store && bureauApi) {
        store.dispatch(bureauApi.util.invalidateTags(tags));
      }
    } catch {
      // bureauApi not available (e.g., during initial load)
    }
  }

  /**
   * Show browser push notification when tab is not focused.
   */
  _showBrowserPush(notification) {
    if (!notification || document.hasFocus()) return;
    if (Notification.permission === 'granted') {
      try {
        new Notification(notification.title || 'New Notification', {
          body: notification.message || '',
          icon: '/images/FC Logo 2.png',
          tag: `notif-${notification.id}`,
        });
      } catch { /* ignore */ }
    }
  }

  // ── Reconnection with exponential backoff + jitter ───────

  scheduleReconnect() {
    if (this.reconnectTimeout || !this.userId) return;

    this.reconnectAttempts++;
    const baseDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    const jitter = Math.random() * 1000; // Prevent thundering herd
    const delay = baseDelay + jitter;

    console.log(`[Realtime] Reconnecting in ${(delay / 1000).toFixed(1)}s (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, delay);
  }

  // ── Tab visibility management ────────────────────────────

  setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Disconnect after 5 minutes in background
        this.visibilityTimeout = setTimeout(() => {
          console.log('[Realtime] Tab hidden >5min, disconnecting');
          this.disconnect();
        }, 5 * 60 * 1000);
      } else {
        clearTimeout(this.visibilityTimeout);
        this.visibilityTimeout = null;

        if (!this.isConnected) {
          console.log('[Realtime] Tab focused, reconnecting + invalidating caches');
          this.reconnectAttempts = 0;
          this.connect();
          // Invalidate all caches on tab refocus
          this._invalidateTags([
            'Notification', 'NotificationCount',
            'Task', 'MyTasks', 'TaskStats',
            'SupportTicket',
            'Broadcast',
          ]);
        }
      }
    });
  }

  // ── Heartbeat ────────────────────────────────────────────

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // ── Client→Server actions ────────────────────────────────

  markAsRead(notificationId) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'mark_read', notification_id: notificationId }));
    }
  }

  acknowledgeBroadcast(broadcastId) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'acknowledge', broadcast_id: broadcastId }));
    }
  }

  // ── Lifecycle ────────────────────────────────────────────

  disconnect() {
    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = null;
    clearTimeout(this.visibilityTimeout);
    this.visibilityTimeout = null;
    this.stopHeartbeat();
    this.userId = null;

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnect on intentional close
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;

    // Dispatch connection state updates asynchronously to avoid
    // "dispatch inside reducer" errors when called from authSlice.logout
    setTimeout(() => {
      try {
        const store = getStore();
        if (store) {
          store.dispatch({ type: 'realtime/setConnected', payload: false });
          store.dispatch({ type: 'notifications/setConnected', payload: false });
          store.dispatch({ type: 'tasks/setConnected', payload: false });
          store.dispatch({ type: 'tickets/setConnected', payload: false });
          store.dispatch({ type: 'broadcasts/setConnected', payload: false });
        }
      } catch {
        // Store may be torn down during logout — safe to ignore
      }
    }, 0);
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      readyState: this.ws?.readyState,
      reconnectAttempts: this.reconnectAttempts,
    };
  }
}

// Module-level singleton
const realtimeService = new RealtimeService();
export default realtimeService;
